import { faAngleRight, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { FieldError } from '@spike/model';
import clsx from 'clsx';
import { Button, Pagination } from 'components/UI';
import exportFromJSON from 'export-from-json';
import { useMasterData } from 'hooks';
import WizardPage from 'pages/WizardPage';
import Papa from 'papaparse';
import { CSSProperties, Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Img } from 'react-image';
import { useSelector } from 'react-redux';
import { uploadFileThunk } from '../../actions/bulkUpload/bulkUploadActions';
import { BulkUploadState } from '../../reducers/bulkUpload/BulkUploadState';
import { RootState } from '../../store';
import ProgressBar from '../Payments/Sidebar/ProgressBar';

// Must match DataExchange::ResourceColumns::WIDE_SLOT_MAX in the backend
const MAX_VACCINE_SLOTS = 10;
const VACCINE_SLOTS = Array.from({ length: MAX_VACCINE_SLOTS }, (_, index) => index + 1);

const VACCINE_FIELDS = VACCINE_SLOTS.flatMap(slot => [`vaccine_name_${slot}`, `vaccine_expiration_date_${slot}`]);

// Accepted CSV headers (normalized to lowercase) for vaccine_name_N / vaccine_expiration_date_N
const VACCINE_FIELD_MAPPING: { [key: string]: string } = Object.fromEntries(
    VACCINE_SLOTS.flatMap(slot => [
        [`vaccine_name_${slot}`, `vaccine_name_${slot}`],
        [`vaccinename${slot}`, `vaccine_name_${slot}`],
        [`vaccine name ${slot}`, `vaccine_name_${slot}`],
        [`vaccine_expiration_date_${slot}`, `vaccine_expiration_date_${slot}`],
        [`vaccineexpirationdate${slot}`, `vaccine_expiration_date_${slot}`],
        [`vaccine expiration date ${slot}`, `vaccine_expiration_date_${slot}`]
    ])
);

// "name (expiration)" for every vaccine slot that has both values, in slot order
const getVaccineLabels = (row: ClientCsv): Array<string> =>
    VACCINE_SLOTS.flatMap(slot => {
        const name = row[`vaccine_name_${slot}` as keyof ClientCsv];
        const expirationDate = row[`vaccine_expiration_date_${slot}` as keyof ClientCsv];
        return name && expirationDate ? [`${name} (${expirationDate})`] : [];
    });

// Empty vaccine columns from `fromSlot` up to MAX_VACCINE_SLOTS, used to fill the CSV template rows
const emptyVaccineColumns = (fromSlot: number): { [key: string]: string } =>
    Object.fromEntries(
        VACCINE_SLOTS.filter(slot => slot >= fromSlot).flatMap(slot => [
            [`vaccine_name_${slot}`, ''],
            [`vaccine_expiration_date_${slot}`, '']
        ])
    );

export interface ClientCsv {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
    pet_name?: string;
    pet_type?: string;
    pet_size?: string;
    pet_breed?: string;
    pet_gender?: string;
    pet_birthdate?: string;
    pet_color?: string;
    // pet_notes?: string; // Deprecated: Use pet_notes_1 to pet_notes_4 instead
    vet_name?: string;
    vet_business_name?: string;
    vet_email?: string;
    vet_phone?: string;
    vaccine_name_1?: string;
    vaccine_expiration_date_1?: string;
    vaccine_name_2?: string;
    vaccine_expiration_date_2?: string;
    vaccine_name_3?: string;
    vaccine_expiration_date_3?: string;
    vaccine_name_4?: string;
    vaccine_expiration_date_4?: string;
    vaccine_name_5?: string;
    vaccine_expiration_date_5?: string;
    vaccine_name_6?: string;
    vaccine_expiration_date_6?: string;
    vaccine_name_7?: string;
    vaccine_expiration_date_7?: string;
    vaccine_name_8?: string;
    vaccine_expiration_date_8?: string;
    vaccine_name_9?: string;
    vaccine_expiration_date_9?: string;
    vaccine_name_10?: string;
    vaccine_expiration_date_10?: string;
    // New fields for enhanced client import
    address_line_two?: string;
    pet_weight?: string;
    pet_notes_1?: string;
    pet_notes_2?: string;
    pet_notes_3?: string;
    pet_notes_4?: string;
    // Authorized contacts (1-4)
    authorized_contact_first_name_1?: string;
    authorized_contact_last_name_1?: string;
    authorized_contact_email_1?: string;
    authorized_contact_phone_1?: string;
    authorized_contact_relationship_id_1?: string;
    authorized_contact_emergency_contact_1?: string;
    authorized_contact_first_name_2?: string;
    authorized_contact_last_name_2?: string;
    authorized_contact_email_2?: string;
    authorized_contact_phone_2?: string;
    authorized_contact_relationship_id_2?: string;
    authorized_contact_emergency_contact_2?: string;
    authorized_contact_first_name_3?: string;
    authorized_contact_last_name_3?: string;
    authorized_contact_email_3?: string;
    authorized_contact_phone_3?: string;
    authorized_contact_relationship_id_3?: string;
    authorized_contact_emergency_contact_3?: string;
    authorized_contact_first_name_4?: string;
    authorized_contact_last_name_4?: string;
    authorized_contact_email_4?: string;
    authorized_contact_phone_4?: string;
    authorized_contact_relationship_id_4?: string;
    authorized_contact_emergency_contact_4?: string;
}

export interface ObjectCsv {
    data: Array<ClientCsv>;
    errors: Array<any>;
    meta: any;
}

const styles = {
    zone: {
        alignItems: 'center',
        border: `2px dashed #CCC`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 20
    } as CSSProperties,
    progressBar: {
        marginTop: 10,
        marginBottom: 10
    } as CSSProperties,
    fileContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #000000',
        borderRadius: '14px',
        padding: 20
    } as CSSProperties,
    fileName: {
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '100%',
        color: '#000000',
        marginBottom: '5px'
    } as CSSProperties,
    fileSize: {
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '100%',
        color: '#7A7A7A'
    } as CSSProperties,
    iconCenter: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    } as CSSProperties,
    fileSubcontainer: {
        display: 'flex',
        width: '80%'
    } as CSSProperties,
    fileSubcontainerDelete: {
        gap: 7,
        display: 'flex',
        justifyContent: 'end',
        width: '20%',
        cursor: 'pointer'
    } as CSSProperties,
    deleteText: {
        fontSize: 13,
        fontWeight: 500,
        lineHeight: '34px',
        color: '#7A7A7A',
        alignItems: 'center',
        display: 'flex'
    } as CSSProperties,
    downloadText: {
        color: '#EAB464',
        cursor: 'pointer',
        textDecoration: 'underline'
    } as CSSProperties,
    clientList: {
        display: 'flex',
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '100%',
        color: '#000000',
        paddingTop: 20,
        paddingBottom: 20,
        border: '1px solid #DDDDDD',
        borderTopStyle: 'unset',
        borderLeftStyle: 'unset',
        borderRightStyle: 'unset',
        width: '100%'
    } as CSSProperties,
    clientListHeader: {
        display: 'flex',
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#222222',
        paddingTop: 20,
        paddingBottom: 20,
        border: '2px solid #000000',
        borderTopStyle: 'unset',
        borderLeftStyle: 'unset',
        borderRightStyle: 'unset',
        width: '100%'
    } as CSSProperties,
    trTableHeader: {
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#222222'
    } as CSSProperties,
    thTable: {
        padding: 20,
        paddingTop: 20,
        paddingBottom: 20,
        textAlign: 'start',
        border: '2px solid #000000',
        borderTopStyle: 'unset',
        borderLeftStyle: 'unset',
        borderRightStyle: 'unset'
    } as CSSProperties,
    trTableContent: {
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '100%',
        color: '#000000'
    } as CSSProperties,
    tdTable: {
        padding: 20,
        paddingTop: 20,
        paddingBottom: 20,
        textAlign: 'start',
        border: '1px solid #DDDDDD',
        borderTopStyle: 'unset',
        borderLeftStyle: 'unset',
        borderRightStyle: 'unset'
    } as CSSProperties,
    vaccineLabel: {
        whiteSpace: 'nowrap'
    } as CSSProperties,
    tableStyle: {
        borderCollapse: 'collapse',
        width: '100%',
        whiteSpace: 'nowrap'
    } as CSSProperties
};

interface ClientBulkUploadProps {
    onClose?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        borderContainer: {
            'borderRadius': 24,
            'marginBottom': 32,
            'padding': '30px 25px',
            'border': '1px solid #DDDDDD',

            [theme.breakpoints.up('md')]: {
                padding: 40,
                marginBottom: 40
            },

            '& .delete-button': {
                [theme.breakpoints.down('sm')]: {
                    height: 38,
                    fontSize: 16,
                    color: '#000',
                    borderRadius: '50%',
                    width: '38px !important',
                    backgroundColor: '#F1F1F1',
                    justifyContent: 'center !important'
                }
            },
            '& .delete-label': {
                [theme.breakpoints.down('sm')]: {
                    display: 'none !important'
                }
            }
        },
        errorMessage: {
            marginBottom: 32,
            marginLeft: '0px !important',

            [theme.breakpoints.up('md')]: {
                marginBottom: 40
            }
        },
        borderContainerContact: {
            display: 'flex',
            padding: '20px 16px',
            borderRadius: '24px',
            border: '1px solid #DDDDDD',

            [theme.breakpoints.up('md')]: {
                padding: '20px'
            }
        },
        firstText: {
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 600,
            color: '#222222',
            marginBottom: 12,

            [theme.breakpoints.up('md')]: {
                fontSize: 20,
                marginBottom: 20
            }
        },
        secondText: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.4,
            color: '#000000',
            marginBottom: 30,

            [theme.breakpoints.up('md')]: {
                fontSize: 15
            }
        },
        thirdText: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
            color: '#000000',
            marginBottom: 16,
            textAlign: 'center',
            justifySelf: 'center',

            [theme.breakpoints.up('md')]: {
                fontSize: 15,
                marginBottom: 30
            }
        },
        csvButton: {
            height: 'auto'
        },
        image: {
            width: 47,
            height: 47,
            marginBottom: 16,

            [theme.breakpoints.up('md')]: {
                marginBottom: 30
            }
        },
        firstHelpText: {
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 600,
            color: '#000000',
            margin: '10px 0px',

            [theme.breakpoints.up('md')]: {
                fontSize: 20
            }
        },
        secondHelpText: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.4,
            color: '#000000',
            marginBottom: 16,

            [theme.breakpoints.up('md')]: {
                fontSize: 15
            }
        },
        thirdHelpText: {
            'fontSize': 14,
            'fontWeight': 600,
            'lineHeight': 1.4,
            'color': '#000000',

            [theme.breakpoints.up('md')]: {
                fontSize: 15
            },

            '& svg': {
                marginLeft: 7
            }
        },
        imageContact: {
            width: '41px',
            height: '41px',
            marginRight: '10px'
        },
        fourthText: {
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 600,
            color: '#222222',
            marginBottom: 16,
            marginTop: 24,

            [theme.breakpoints.up('md')]: {
                fontSize: 20
            }
        },
        checkFile: {
            width: '20px',
            height: '20px',
            marginRight: '10px'
        },
        iconFile: {
            width: '22px',
            height: '29px',
            marginRight: '10px'
        },
        iconDelete: {
            width: '17px',
            height: '17px'
        },
        tableContainer: {
            backgroundColor: 'white',

            [theme.breakpoints.down('sm')]: {
                width: 'auto',
                margin: '0px -16px'
            }
        },
        scrollArrow: {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        boxGridContainer: {
            'width': '100%',
            'overflowX': 'scroll',
            //overflowY: 'hidden',

            [theme.breakpoints.up('md')]: {
                overflow: 'hidden',
                paddingBottom: '20px'
            },
            [theme.breakpoints.down('sm')]: {
                '& th': {
                    fontSize: 14,
                    lineHeight: 1,
                    fontWeight: 600,
                    borderWidth: '1px !important',
                    borderBottomColor: '#D4D4D4 !important'
                },
                '& td, & th': {
                    '&:first-child': {
                        'left': 0,
                        'position': 'sticky',

                        '&:before': {
                            content: `''`,
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: -1,
                            display: 'block',
                            position: 'absolute',
                            backgroundColor: '#fff',
                            boxShadow: '0px 0px 0px 0px transparent',
                            transition: 'box-shadow 0.15s ease-out'
                        }
                    }
                }
            },

            '&.scrolled td:first-child, &.scrolled th:first-child': {
                '&:before': {
                    boxShadow: '8px 0px 10px 0px rgba(0, 0, 0, 0.08)'
                }
            }
        },
        pagination: {
            [theme.breakpoints.down('sm')]: {
                paddingRight: '9px'
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: '15px'
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: '20px'
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: '30px'
            }
        },
        tableContainerGrid: {
            [theme.breakpoints.up('md')]: {
                padding: '30px'
            }
        },
        arrowIcon: {
            cursor: 'pointer'
        },
        button: {
            'height': 47,
            'width': 160,
            'justifySelf': 'center',

            '& + &': {
                marginLeft: 10
            },

            [theme.breakpoints.down('sm')]: {
                width: '100%'
            }
        },
        statusPage: {
            gap: 20,
            height: '100%',
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: '0px 16px'
        },
        statusBody: {
            maxWidth: 460,

            [theme.breakpoints.down('sm')]: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }
        },
        statusIcon: {
            width: 83,
            height: 83,
            marginBottom: 16
        },
        statusTitle: {
            fontSize: 26,
            fontWeight: 600,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                fontSize: 32
            }
        },
        statusDescription: {
            fontSize: 16,
            lineHeight: 1.4,

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            }
        },
        statusFooter: {
            width: '100%',

            [theme.breakpoints.down('sm')]: {
                paddingBottom: 24
            }
        },
        progressBar: {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        }
    })
);

const errorStyle = {
    color: '#EF4F57',
    fontSize: 14,
    fontWeight: 500,
    marginLeft: '16px'
};

// Función para intentar arreglar líneas CSV con columnas extra
const fixCSVLine = (line: string, expectedColumns: number): string => {
    const values = line.split(',');

    if (values.length <= expectedColumns) {
        return line; // No hay problema
    }

    // Intentar encontrar valores que probablemente deberían estar unidos
    // (valores que empiezan con espacio después de una coma)
    const fixedValues: string[] = [];
    let i = 0;

    while (i < values.length && fixedValues.length < expectedColumns) {
        let currentValue = values[i];

        // Si no es la última columna esperada, revisar si el siguiente valor
        // debería ser unido (empieza con espacio)
        if (fixedValues.length < expectedColumns - 1) {
            let j = i + 1;
            while (j < values.length && values[j].startsWith(' ')) {
                currentValue += ',' + values[j];
                j++;
            }
            i = j;
        } else {
            // Para la última columna, unir todo lo que queda
            for (let k = i + 1; k < values.length; k++) {
                currentValue += ',' + values[k];
            }
            i = values.length;
        }

        fixedValues.push(currentValue);
    }

    // Si aún faltan columnas, agregar vacías
    while (fixedValues.length < expectedColumns) {
        fixedValues.push('');
    }

    return fixedValues.join(',');
};

// Utility function to format file size
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const ClientBulkUpload: FunctionComponent<ClientBulkUploadProps> = props => {
    const classes = useStyles();
    const masterData = useMasterData();

    const [step, setStep] = useState<number>(1);
    const [saving] = useState(false);
    const [parsedCsvData, setParsedCsvData] = useState<Array<ClientCsv>>([]);
    const [file, setFile] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [arrowLeft, setArrowLeft] = useState(false);
    const [arrowRight, setArrowRight] = useState(true);
    const elementRef = useRef<HTMLInputElement>(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [icon, setIcon] = useState('');
    const [uploading, setUploading] = useState(false);
    const [textButton, setTextButton] = useState('');
    const [importInProgress, setImportInProgress] = useState(false);
    const progress = useSelector<RootState, BulkUploadState>(state => state.bulkUpload);

    // Ref to track when we just started a new import to ignore stale events
    const justStartedImportRef = useRef(false);

    const [scrollPos, setScrollPos] = useState(0);

    const mandatoryListFields = [
        'first_name*',
        'last_name*',
        'phone*',
        'email*',
        'pet_name*',
        'pet_size*',
        'pet_type*'
    ];

    // Import status constants to avoid duplication
    const IMPORT_STATUS = {
        IMPORTING: {
            title: 'Importing your clients...',
            content:
                'We are currently importing your clients. Please do not close the window and wait for the process to finish. This may take a few moments.',
            icon: '/images/clients/uploading.png',
            button: 'Cancel'
        },
        SUCCESS: {
            title: 'Import has been successfully completed',
            icon: '/images/clients/uploaded.png',
            button: 'Done'
        }
    } as const;

    // Reset all import-related states to initial values
    const resetImportState = () => {
        justStartedImportRef.current = false; // Reset flag
        setUploading(false);
        setImportInProgress(false);
        setStep(1);
        setFile(false);
        setSelectedFile(null);
        setParsedCsvData([]);
        setErrors([]);
        setTitle('');
        setContent('');
        setIcon('');
        setTextButton('');
    };

    // Set UI to importing status
    const setImportingStatus = () => {
        setTitle(IMPORT_STATUS.IMPORTING.title);
        setContent(IMPORT_STATUS.IMPORTING.content);
        setIcon(IMPORT_STATUS.IMPORTING.icon);
        setTextButton(IMPORT_STATUS.IMPORTING.button);
    };

    // Set UI to success status
    const setSuccessStatus = (successfullyImported: number, errorCount: number) => {
        setTitle(IMPORT_STATUS.SUCCESS.title);
        const errors = errorCount ? `${errorCount} with errors` : '';
        setContent(`${successfullyImported} clients have been added to your client list. ${errors}`);
        setIcon(IMPORT_STATUS.SUCCESS.icon);
        setTextButton(IMPORT_STATUS.SUCCESS.button);
    };

    // Helper function to normalize color strings for comparison
    const normalizeColorString = (str: string): string => {
        return str
            .trim()
            .replace(/\s+/g, ' ') // Multiple spaces → single space
            .replace(/&amp;/gi, '&') // HTML entity → ampersand
            .toLowerCase();
    };

    // Validate pet_color against master data based on pet_type
    const isValidColor = (color: string | undefined, petType: string | undefined): boolean => {
        if (!color || !petType) return true; // Empty color is valid (optional field)

        const colorNormalized = normalizeColorString(color);
        let validColors: Array<{ id: string; name: string }> = [];

        if (petType === 'dogs') {
            validColors = masterData.dogColors || [];
        } else if (petType === 'cats') {
            validColors = masterData.catColors || [];
        } else if (petType === 'exotics') {
            validColors = masterData.exoticColors || [];
        }

        // Check if color matches by id or name (normalized comparison)
        return validColors.some(
            c => c.id.toLowerCase() === colorNormalized || normalizeColorString(c.name) === colorNormalized
        );
    };

    // Find the color id from master data based on color name/id input
    const findColorId = (colorInput: string, petType: string | undefined): string | null => {
        if (!colorInput || !petType) return null;

        const colorNormalized = normalizeColorString(colorInput);
        let validColors: Array<{ id: string; name: string }> = [];

        if (petType === 'dogs') {
            validColors = masterData.dogColors || [];
        } else if (petType === 'cats') {
            validColors = masterData.catColors || [];
        } else if (petType === 'exotics') {
            validColors = masterData.exoticColors || [];
        }

        // Find matching color and return its id
        const matchedColor = validColors.find(
            c => c.id.toLowerCase() === colorNormalized || normalizeColorString(c.name) === colorNormalized
        );

        return matchedColor ? matchedColor.id : null;
    };

    const nextHandler = () => {
        const errors = validate();
        setErrors(errors);
        if (errors.length === 0) {
            setStep(prev => prev + 1);
        }
    };

    // Calculate if there are any invalid colors in the data
    const hasInvalidColors = useMemo(() => {
        return parsedCsvData.some(
            row => row.pet_color && row.pet_color.trim() !== '' && !isValidColor(row.pet_color, row.pet_type)
        );
    }, [parsedCsvData]);

    useEffect(() => {
        // Skip stale events from previous imports if no active import
        if (!importInProgress) {
            justStartedImportRef.current = false; // Reset flag
            return;
        }

        // Ignore stale 'import_finished' event when we just started a new import
        if (justStartedImportRef.current && progress.event === 'import_finished') {
            justStartedImportRef.current = false; // Clear flag
            return;
        }

        if (progress.event == 'record_imported' || progress.event == 'import_started') {
            justStartedImportRef.current = false; // Clear flag once real backend event arrives
            setImportingStatus();
        } else if (progress.event == 'import_finished') {
            setSuccessStatus(progress.successfully_imported || 0, progress.errors || 0);
            setImportInProgress(false); // Reset flag when import completes
        }
    }, [progress, importInProgress]);

    // Handler to process CSV file using Papa.parse
    const handleFileParse = (file: File) => {
        const reader = new FileReader();

        reader.onload = e => {
            const text = e.target?.result as string;

            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                delimiter: ',',
                quoteChar: '"',
                escapeChar: '"',
                transformHeader: (header: string) => {
                    // Only clean headers and remove asterisks
                    return header.replace('*', '').trim();
                },
                complete: results => {
                    setFile(true);
                    setErrors([]);

                    console.log('Raw results:', results);

                    // Map Papa.parse results to ObjectCsv format
                    const parsedResults: ObjectCsv = {
                        data: results.data as Array<ClientCsv>,
                        errors: results.errors || [],
                        meta: results.meta || {}
                    };

                    // Process data (existing logic)
                    const cleanedData = parsedResults.data
                        .map((row: any, index: number) => {
                            const cleanedRow: any = {};

                            // Define mapping of possible headers to expected fields (now case-insensitive)
                            const fieldMapping: { [key: string]: string } = {
                                'first_name': 'first_name',
                                'firstname': 'first_name',
                                'first name': 'first_name',
                                'last_name': 'last_name',
                                'lastname': 'last_name',
                                'last name': 'last_name',
                                'email': 'email',
                                'phone': 'phone',
                                'address': 'address',
                                'city': 'city',
                                'state': 'state',
                                'zipcode': 'zipcode',
                                'zip code': 'zipcode',
                                'zip_code': 'zipcode',
                                'country': 'country',
                                'pet_name': 'pet_name',
                                'petname': 'pet_name',
                                'pet name': 'pet_name',
                                'pet_type': 'pet_type',
                                'pettype': 'pet_type',
                                'pet type': 'pet_type',
                                'pet_size': 'pet_size',
                                'petsize': 'pet_size',
                                'pet size': 'pet_size',
                                'pet_breed': 'pet_breed',
                                'petbreed': 'pet_breed',
                                'pet breed': 'pet_breed',
                                'pet_gender': 'pet_gender',
                                'petgender': 'pet_gender',
                                'pet gender': 'pet_gender',
                                'pet_birthdate': 'pet_birthdate',
                                'petbirthdate': 'pet_birthdate',
                                'pet birthdate': 'pet_birthdate',
                                'pet_birth_date': 'pet_birthdate',
                                'pet birth date': 'pet_birthdate',
                                'pet_dob': 'pet_birthdate',
                                'petdob': 'pet_birthdate',
                                'pet dob': 'pet_birthdate',
                                'date_of_birth': 'pet_birthdate',
                                'dateofbirth': 'pet_birthdate',
                                'date of birth': 'pet_birthdate',
                                'pet_color': 'pet_color',
                                'petcolor': 'pet_color',
                                'pet color': 'pet_color',
                                'color': 'pet_color',
                                'hair_color': 'pet_color',
                                'haircolor': 'pet_color',
                                'hair color': 'pet_color',
                                'pet_hair_color': 'pet_color',
                                'pethaircolor': 'pet_color',
                                'pet hair color': 'pet_color',
                                // Deprecated: Use pet_notes_1 to pet_notes_4 instead
                                // 'pet_notes': 'pet_notes',
                                // 'petnotes': 'pet_notes',
                                // 'pet notes': 'pet_notes',
                                'vet_name': 'vet_name',
                                'vetname': 'vet_name',
                                'vet name': 'vet_name',
                                'vet_business_name': 'vet_business_name',
                                'vetbusinessname': 'vet_business_name',
                                'vet business name': 'vet_business_name',
                                'vet_email': 'vet_email',
                                'vetemail': 'vet_email',
                                'vet email': 'vet_email',
                                'vet_phone': 'vet_phone',
                                'vetphone': 'vet_phone',
                                'vet phone': 'vet_phone',
                                ...VACCINE_FIELD_MAPPING,
                                // New field mappings
                                'address_line_two': 'address_line_two',
                                'address line two': 'address_line_two',
                                'address line 2': 'address_line_two',
                                'addresslinetwo': 'address_line_two',
                                'address2': 'address_line_two',
                                'pet_weight': 'pet_weight',
                                'petweight': 'pet_weight',
                                'pet weight': 'pet_weight',
                                'weight': 'pet_weight',
                                'pet_notes_1': 'pet_notes_1',
                                'petnotes1': 'pet_notes_1',
                                'pet notes 1': 'pet_notes_1',
                                'pet_notes_2': 'pet_notes_2',
                                'petnotes2': 'pet_notes_2',
                                'pet notes 2': 'pet_notes_2',
                                'pet_notes_3': 'pet_notes_3',
                                'petnotes3': 'pet_notes_3',
                                'pet notes 3': 'pet_notes_3',
                                'pet_notes_4': 'pet_notes_4',
                                'petnotes4': 'pet_notes_4',
                                'pet notes 4': 'pet_notes_4',
                                // Authorized contact 1
                                'authorized_contact_first_name_1': 'authorized_contact_first_name_1',
                                'authorized contact first name 1': 'authorized_contact_first_name_1',
                                'authorizedcontactfirstname1': 'authorized_contact_first_name_1',
                                'authorized_contact_last_name_1': 'authorized_contact_last_name_1',
                                'authorized contact last name 1': 'authorized_contact_last_name_1',
                                'authorizedcontactlastname1': 'authorized_contact_last_name_1',
                                'authorized_contact_email_1': 'authorized_contact_email_1',
                                'authorized contact email 1': 'authorized_contact_email_1',
                                'authorizedcontactemail1': 'authorized_contact_email_1',
                                'authorized_email_1': 'authorized_contact_email_1',
                                'authorized email 1': 'authorized_contact_email_1',
                                'contact_email_1': 'authorized_contact_email_1',
                                'contact email 1': 'authorized_contact_email_1',
                                'authorized_contact_phone_1': 'authorized_contact_phone_1',
                                'authorized contact phone 1': 'authorized_contact_phone_1',
                                'authorizedcontactphone1': 'authorized_contact_phone_1',
                                'authorized_contact_relationship_id_1': 'authorized_contact_relationship_id_1',
                                'authorized contact relationship id 1': 'authorized_contact_relationship_id_1',
                                'authorized contact relationship 1': 'authorized_contact_relationship_id_1',
                                'authorizedcontactrelationshipid1': 'authorized_contact_relationship_id_1',
                                'authorized_contact_emergency_contact_1': 'authorized_contact_emergency_contact_1',
                                'authorized contact emergency contact 1': 'authorized_contact_emergency_contact_1',
                                'authorized contact emergency 1': 'authorized_contact_emergency_contact_1',
                                'authorizedcontactemergencycontact1': 'authorized_contact_emergency_contact_1',
                                // Authorized contact 2
                                'authorized_contact_first_name_2': 'authorized_contact_first_name_2',
                                'authorized contact first name 2': 'authorized_contact_first_name_2',
                                'authorizedcontactfirstname2': 'authorized_contact_first_name_2',
                                'authorized_contact_last_name_2': 'authorized_contact_last_name_2',
                                'authorized contact last name 2': 'authorized_contact_last_name_2',
                                'authorizedcontactlastname2': 'authorized_contact_last_name_2',
                                'authorized_contact_email_2': 'authorized_contact_email_2',
                                'authorized contact email 2': 'authorized_contact_email_2',
                                'authorizedcontactemail2': 'authorized_contact_email_2',
                                'authorized_email_2': 'authorized_contact_email_2',
                                'authorized email 2': 'authorized_contact_email_2',
                                'contact_email_2': 'authorized_contact_email_2',
                                'contact email 2': 'authorized_contact_email_2',
                                'authorized_contact_phone_2': 'authorized_contact_phone_2',
                                'authorized contact phone 2': 'authorized_contact_phone_2',
                                'authorizedcontactphone2': 'authorized_contact_phone_2',
                                'authorized_contact_relationship_id_2': 'authorized_contact_relationship_id_2',
                                'authorized contact relationship id 2': 'authorized_contact_relationship_id_2',
                                'authorized contact relationship 2': 'authorized_contact_relationship_id_2',
                                'authorizedcontactrelationshipid2': 'authorized_contact_relationship_id_2',
                                'authorized_contact_emergency_contact_2': 'authorized_contact_emergency_contact_2',
                                'authorized contact emergency contact 2': 'authorized_contact_emergency_contact_2',
                                'authorized contact emergency 2': 'authorized_contact_emergency_contact_2',
                                'authorizedcontactemergencycontact2': 'authorized_contact_emergency_contact_2',
                                // Authorized contact 3
                                'authorized_contact_first_name_3': 'authorized_contact_first_name_3',
                                'authorized contact first name 3': 'authorized_contact_first_name_3',
                                'authorizedcontactfirstname3': 'authorized_contact_first_name_3',
                                'authorized_contact_last_name_3': 'authorized_contact_last_name_3',
                                'authorized contact last name 3': 'authorized_contact_last_name_3',
                                'authorizedcontactlastname3': 'authorized_contact_last_name_3',
                                'authorized_contact_email_3': 'authorized_contact_email_3',
                                'authorized contact email 3': 'authorized_contact_email_3',
                                'authorizedcontactemail3': 'authorized_contact_email_3',
                                'authorized_email_3': 'authorized_contact_email_3',
                                'authorized email 3': 'authorized_contact_email_3',
                                'contact_email_3': 'authorized_contact_email_3',
                                'contact email 3': 'authorized_contact_email_3',
                                'authorized_contact_phone_3': 'authorized_contact_phone_3',
                                'authorized contact phone 3': 'authorized_contact_phone_3',
                                'authorizedcontactphone3': 'authorized_contact_phone_3',
                                'authorized_contact_relationship_id_3': 'authorized_contact_relationship_id_3',
                                'authorized contact relationship id 3': 'authorized_contact_relationship_id_3',
                                'authorized contact relationship 3': 'authorized_contact_relationship_id_3',
                                'authorizedcontactrelationshipid3': 'authorized_contact_relationship_id_3',
                                'authorized_contact_emergency_contact_3': 'authorized_contact_emergency_contact_3',
                                'authorized contact emergency contact 3': 'authorized_contact_emergency_contact_3',
                                'authorized contact emergency 3': 'authorized_contact_emergency_contact_3',
                                'authorizedcontactemergencycontact3': 'authorized_contact_emergency_contact_3',
                                // Authorized contact 4
                                'authorized_contact_first_name_4': 'authorized_contact_first_name_4',
                                'authorized contact first name 4': 'authorized_contact_first_name_4',
                                'authorizedcontactfirstname4': 'authorized_contact_first_name_4',
                                'authorized_contact_last_name_4': 'authorized_contact_last_name_4',
                                'authorized contact last name 4': 'authorized_contact_last_name_4',
                                'authorizedcontactlastname4': 'authorized_contact_last_name_4',
                                'authorized_contact_email_4': 'authorized_contact_email_4',
                                'authorized contact email 4': 'authorized_contact_email_4',
                                'authorizedcontactemail4': 'authorized_contact_email_4',
                                'authorized_email_4': 'authorized_contact_email_4',
                                'authorized email 4': 'authorized_contact_email_4',
                                'contact_email_4': 'authorized_contact_email_4',
                                'contact email 4': 'authorized_contact_email_4',
                                'authorized_contact_phone_4': 'authorized_contact_phone_4',
                                'authorized contact phone 4': 'authorized_contact_phone_4',
                                'authorizedcontactphone4': 'authorized_contact_phone_4',
                                'authorized_contact_relationship_id_4': 'authorized_contact_relationship_id_4',
                                'authorized contact relationship id 4': 'authorized_contact_relationship_id_4',
                                'authorized contact relationship 4': 'authorized_contact_relationship_id_4',
                                'authorizedcontactrelationshipid4': 'authorized_contact_relationship_id_4',
                                'authorized_contact_emergency_contact_4': 'authorized_contact_emergency_contact_4',
                                'authorized contact emergency contact 4': 'authorized_contact_emergency_contact_4',
                                'authorized contact emergency 4': 'authorized_contact_emergency_contact_4',
                                'authorizedcontactemergencycontact4': 'authorized_contact_emergency_contact_4'
                            };

                            // Process each field in the row
                            Object.keys(row).forEach(key => {
                                const normalizedKey = key.toLowerCase().trim(); // Normalize for comparison
                                const value = row[key];

                                // Skip fields that start with "_" (auto-generated fields)
                                if (normalizedKey.startsWith('_')) {
                                    console.warn(`Skipping auto-generated field: ${key} in row ${index + 1}`);
                                    return;
                                }

                                // Map to the correct field using the normalized key
                                const targetField = fieldMapping[normalizedKey];
                                if (targetField) {
                                    // Clean the value
                                    let cleanValue = '';
                                    if (value !== null && value !== undefined) {
                                        cleanValue = value.toString().trim();
                                        // If the value seems to be an auto-generated field, convert to empty
                                        if (cleanValue.match(/^_\d+$/)) {
                                            cleanValue = '';
                                        }
                                    }
                                    cleanedRow[targetField] = cleanValue;
                                } else {
                                    console.warn(`Unknown field: ${key} in row ${index + 1}`);
                                }
                            });

                            // Asegurar que todos los campos esperados existan
                            const expectedFields = [
                                'first_name',
                                'last_name',
                                'email',
                                'phone',
                                'address',
                                'city',
                                'state',
                                'zipcode',
                                'country',
                                'pet_name',
                                'pet_type',
                                'pet_size',
                                'pet_breed',
                                'pet_gender',
                                'pet_birthdate',
                                'pet_color',
                                // 'pet_notes', // Deprecated: Use pet_notes_1 to pet_notes_4 instead
                                'vet_name',
                                'vet_business_name',
                                'vet_email',
                                'vet_phone',
                                ...VACCINE_FIELDS,
                                // New fields
                                'address_line_two',
                                'pet_weight',
                                'pet_notes_1',
                                'pet_notes_2',
                                'pet_notes_3',
                                'pet_notes_4',
                                'authorized_contact_first_name_1',
                                'authorized_contact_last_name_1',
                                'authorized_contact_email_1',
                                'authorized_contact_phone_1',
                                'authorized_contact_relationship_id_1',
                                'authorized_contact_emergency_contact_1',
                                'authorized_contact_first_name_2',
                                'authorized_contact_last_name_2',
                                'authorized_contact_email_2',
                                'authorized_contact_phone_2',
                                'authorized_contact_relationship_id_2',
                                'authorized_contact_emergency_contact_2',
                                'authorized_contact_first_name_3',
                                'authorized_contact_last_name_3',
                                'authorized_contact_email_3',
                                'authorized_contact_phone_3',
                                'authorized_contact_relationship_id_3',
                                'authorized_contact_emergency_contact_3',
                                'authorized_contact_first_name_4',
                                'authorized_contact_last_name_4',
                                'authorized_contact_email_4',
                                'authorized_contact_phone_4',
                                'authorized_contact_relationship_id_4',
                                'authorized_contact_emergency_contact_4'
                            ];

                            expectedFields.forEach(field => {
                                if (!(field in cleanedRow)) {
                                    cleanedRow[field] = '';
                                }
                            });

                            return cleanedRow;
                        })
                        .filter((row: any) => {
                            // Filter rows that are completely empty or that only have auto-generated fields
                            const values = Object.values(row);
                            const hasValidData = values.some(
                                (value: any) =>
                                    value && value.toString().trim() !== '' && !value.toString().match(/^_\d+$/)
                            );

                            if (!hasValidData) {
                                console.warn('Filtered out empty row');
                            }

                            return hasValidData;
                        });

                    console.log(
                        `Processed ${cleanedData.length} valid rows from ${parsedResults.data.length} total rows`
                    );
                    console.log('Sample cleaned data:', cleanedData.slice(0, 3));

                    setParsedCsvData(cleanedData);
                },
                error: (error: any) => {
                    console.error('Papa.parse error:', error);
                    const errors = validateFormat();
                    setErrors(errors);
                }
            });
        };

        reader.onerror = () => {
            const errors = validateFormat();
            setErrors(errors);
        };

        reader.readAsText(file);
    };

    const saveHandler = () => {
        // Mark that we just started a new import to ignore stale backend events
        justStartedImportRef.current = true;
        // Set uploading UI state immediately to prevent showing stale success message from previous import
        setImportingStatus();
        setImportInProgress(true);
        setUploading(true);
        if (parsedCsvData.length > 0) {
            // Transform data before sending to API
            const transformedData = parsedCsvData.map(row => {
                const transformedRow: ClientCsv = { ...row };

                // Handle vet data - only include if both name and business_name are present
                const hasVetData = row.vet_name?.trim() && row.vet_business_name?.trim();
                if (!hasVetData) {
                    // Remove vet fields if we don't have the minimum required data
                    delete transformedRow.vet_name;
                    delete transformedRow.vet_business_name;
                    delete transformedRow.vet_email;
                    delete transformedRow.vet_phone;
                }

                // Handle vaccine data - only include vaccines where both name and expiration_date are present
                for (let i = 1; i <= MAX_VACCINE_SLOTS; i++) {
                    const vaccineNameKey = `vaccine_name_${i}` as keyof ClientCsv;
                    const vaccineExpirationKey = `vaccine_expiration_date_${i}` as keyof ClientCsv;
                    const vaccineNameValue = row[vaccineNameKey];
                    const vaccineExpirationValue = row[vaccineExpirationKey];
                    const vaccineName = typeof vaccineNameValue === 'string' ? vaccineNameValue.trim() : '';
                    const vaccineExpiration =
                        typeof vaccineExpirationValue === 'string' ? vaccineExpirationValue.trim() : '';

                    if (!vaccineName || !vaccineExpiration) {
                        // Remove vaccine fields if we don't have both name and expiration
                        delete transformedRow[vaccineNameKey];
                        delete transformedRow[vaccineExpirationKey];
                    }
                }

                // Handle pet notes (1-4) - remove if empty
                for (let i = 1; i <= 4; i++) {
                    const petNotesKey = `pet_notes_${i}` as keyof ClientCsv;
                    const petNotesValue = row[petNotesKey];
                    const petNotes = typeof petNotesValue === 'string' ? petNotesValue.trim() : '';

                    if (!petNotes) {
                        delete transformedRow[petNotesKey];
                    }
                }

                // Handle authorized contacts (1-4) - validate and normalize
                for (let i = 1; i <= 4; i++) {
                    const firstNameKey = `authorized_contact_first_name_${i}` as keyof ClientCsv;
                    const lastNameKey = `authorized_contact_last_name_${i}` as keyof ClientCsv;
                    const emailKey = `authorized_contact_email_${i}` as keyof ClientCsv;
                    const phoneKey = `authorized_contact_phone_${i}` as keyof ClientCsv;
                    const relationshipKey = `authorized_contact_relationship_id_${i}` as keyof ClientCsv;
                    const emergencyKey = `authorized_contact_emergency_contact_${i}` as keyof ClientCsv;

                    const firstNameValue = row[firstNameKey];
                    const lastNameValue = row[lastNameKey];
                    const emailValue = row[emailKey];
                    const phoneValue = row[phoneKey];
                    const relationshipValue = row[relationshipKey];
                    const emergencyValue = row[emergencyKey];

                    const firstName = typeof firstNameValue === 'string' ? firstNameValue.trim() : '';
                    const lastName = typeof lastNameValue === 'string' ? lastNameValue.trim() : '';
                    const email = typeof emailValue === 'string' ? emailValue.trim() : '';
                    const phone = typeof phoneValue === 'string' ? phoneValue.trim() : '';
                    const relationshipId = typeof relationshipValue === 'string' ? relationshipValue.trim() : '';
                    const isEmergency = emergencyValue != null ? String(emergencyValue).trim() : '';

                    // Check if we have minimum required data (first name, last name, phone)
                    const hasMinimumData = firstName && lastName && phone;

                    if (!hasMinimumData) {
                        // Remove all contact fields if missing required data
                        delete transformedRow[firstNameKey];
                        delete transformedRow[lastNameKey];
                        delete transformedRow[emailKey];
                        delete transformedRow[phoneKey];
                        delete transformedRow[relationshipKey];
                        delete transformedRow[emergencyKey];
                    } else {
                        // Contact is valid, handle optional email field
                        if (!email) {
                            delete transformedRow[emailKey];
                        }
                        // Validate relationship_id (1-7, default to 3 = Friend)
                        if (relationshipId) {
                            const relationshipNum = parseInt(relationshipId);
                            if (isNaN(relationshipNum) || relationshipNum < 1 || relationshipNum > 7) {
                                console.warn(
                                    `Invalid relationship_id "${relationshipId}" for contact ${i}. Using default (3 = Friend)`
                                );
                                (transformedRow as any)[relationshipKey] = '3';
                            } else {
                                (transformedRow as any)[relationshipKey] = relationshipNum.toString();
                            }
                        } else {
                            (transformedRow as any)[relationshipKey] = '3'; // Default: Friend
                        }

                        // Normalize emergency_contact to string "true" or "false"
                        if (isEmergency) {
                            const normalized = isEmergency.toLowerCase();
                            (transformedRow as any)[emergencyKey] =
                                normalized === 'true' ||
                                normalized === '1' ||
                                normalized === 'yes' ||
                                normalized === 'y' ||
                                normalized === 'si' ||
                                normalized === 's'
                                    ? 'true'
                                    : 'false';
                        } else {
                            (transformedRow as any)[emergencyKey] = 'false';
                        }
                    }
                }

                // Handle pet_color - transform to id format before sending to backend
                if (transformedRow.pet_color && transformedRow.pet_color.trim()) {
                    const colorId = findColorId(transformedRow.pet_color, row.pet_type);
                    if (colorId) {
                        transformedRow.pet_color = colorId; // Transform "Black, Brown & White" → "black_brown_white"
                    }
                    // If colorId is null (invalid color), keep original value and let backend skip it (non-blocking)
                }

                return transformedRow;
            });

            uploadFileThunk(transformedData).then(r => {
                if (r == null || r.status != 200) {
                    setTitle('Error during clients import');
                    setContent(
                        'While processing the bulk upload of clients, an error occurred that prevented the successful completion of the operation.'
                    );
                    setIcon('/images/clients/notUploaded.png');
                    setTextButton('Close');
                }
            });
        }
    };

    const validate = (): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        if (!file) {
            errors.push({
                fieldName: 'file',
                errorMessage: 'Please upload CSV file'
            });
        }

        return errors;
    };

    const validateFormat = (): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        errors.push({
            fieldName: 'file',
            errorMessage: 'File must be on CSV format'
        });

        return errors;
    };

    const [page, setPage] = useState(1);
    const [pageClients, setPageClients] = useState<Array<ClientCsv>>(parsedCsvData.slice(0, 10));
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setPageClients(parsedCsvData.slice(0, 10));
    }, [parsedCsvData]);

    const changePageSizeHandler = (pageSize: number) => {
        setPageSize(pageSize);
        changePage(parsedCsvData, page, pageSize);
    };

    const changePageHandler = (page: number) => {
        setPage(page);
        changePage(parsedCsvData, page, pageSize);
    };

    const changePage = (clients: Array<ClientCsv>, page: number, pageSize: number) => {
        if (pageSize === 0) {
            setPageClients(parsedCsvData);
        } else {
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            setPageClients(parsedCsvData.slice(startIndex, endIndex));
        }
    };

    const pagination = (
        <Pagination
            className={classes.pagination}
            count={parsedCsvData ? parsedCsvData.length : 0}
            page={page}
            pageSize={pageSize}
            onPageChange={changePageHandler}
            onPageSizeChange={changePageSizeHandler}
        />
    );

    const handleHorizantalScrollRight = () => {
        if (elementRef.current != null) {
            elementRef.current.scrollLeft += 500;
            if (elementRef.current.scrollLeft + elementRef.current.offsetWidth + 10 >= elementRef.current.scrollWidth) {
                setArrowRight(false);
            } else {
                setArrowRight(true);
            }
            if (elementRef.current.scrollLeft === 0) {
                setArrowLeft(false);
            } else {
                setArrowLeft(true);
            }
        }
    };

    const handleHorizantalScrollLeft = () => {
        if (elementRef.current != null) {
            elementRef.current.scrollLeft -= 500;
            if (elementRef.current.scrollLeft + elementRef.current.offsetWidth + 10 >= elementRef.current.scrollWidth) {
                setArrowRight(false);
            } else {
                setArrowRight(true);
            }
            if (elementRef.current.scrollLeft === 0) {
                setArrowLeft(false);
            } else {
                setArrowLeft(true);
            }
        }
    };

    const exportToCsv = () => {
        const data = [
            {
                'first_name*': 'Jane',
                'last_name*': 'Doe',
                'email*': 'jane@cuddlesapp.com',
                'phone*': '555-555-5555',
                'address': '123 Main St',
                'address_line_two': 'Apt 4B',
                'city': 'Miami',
                'state': 'FL',
                'zipcode': '33101',
                'country': 'US',
                'pet_name*': 'Paco',
                'pet_type*': 'dogs',
                'pet_size*': 'medium',
                'pet_breed': 'Chow Chow',
                'pet_gender': 'male',
                'pet_birthdate': '2020-05-15',
                'pet_color': 'Black & Tan',
                'pet_weight': '25',
                // 'pet_notes': 'Friendly and energetic', // Deprecated: Use pet_notes_1 to pet_notes_4 instead
                'pet_notes_1': 'Additional vaccination info',
                'pet_notes_2': 'Prefers harness over collar',
                'pet_notes_3': '',
                'pet_notes_4': '',
                'vet_name': 'Dr. Smith',
                'vet_business_name': 'Pets Veterinary Clinic',
                'vet_email': 'drsmith@example.com',
                'vet_phone': '555-123-4567',
                'vaccine_name_1': 'rabies',
                'vaccine_expiration_date_1': '2026-03-12',
                'vaccine_name_2': 'dhpp',
                'vaccine_expiration_date_2': '2026-10-05',
                ...emptyVaccineColumns(3),
                'authorized_contact_first_name_1': 'Maria',
                'authorized_contact_last_name_1': 'Rodriguez',
                'authorized_contact_email_1': 'maria.rodriguez@example.com',
                'authorized_contact_phone_1': '555-111-2222',
                'authorized_contact_relationship_id_1': '2',
                'authorized_contact_emergency_contact_1': 'true',
                'authorized_contact_first_name_2': 'Carlos',
                'authorized_contact_last_name_2': 'Gomez',
                'authorized_contact_email_2': 'carlos.gomez@example.com',
                'authorized_contact_phone_2': '555-333-4444',
                'authorized_contact_relationship_id_2': '3',
                'authorized_contact_emergency_contact_2': 'false',
                'authorized_contact_first_name_3': '',
                'authorized_contact_last_name_3': '',
                'authorized_contact_email_3': '',
                'authorized_contact_phone_3': '',
                'authorized_contact_relationship_id_3': '',
                'authorized_contact_emergency_contact_3': '',
                'authorized_contact_first_name_4': '',
                'authorized_contact_last_name_4': '',
                'authorized_contact_email_4': '',
                'authorized_contact_phone_4': '',
                'authorized_contact_relationship_id_4': '',
                'authorized_contact_emergency_contact_4': ''
            },
            {
                'first_name*': 'Jane',
                'last_name*': 'Doe',
                'email*': 'jane@cuddlesapp.com',
                'phone*': '555-555-5555',
                'address': '123 Main St',
                'address_line_two': 'Apt 4B',
                'city': 'Miami',
                'state': 'FL',
                'zipcode': '33101',
                'country': 'US',
                'pet_name*': 'Tommy',
                'pet_type*': 'dogs',
                'pet_size*': 'large',
                'pet_breed': 'Doodle',
                'pet_gender': 'female',
                'pet_birthdate': '2023-03-20',
                'pet_color': 'buff_white',
                'pet_weight': '60',
                // 'pet_notes': '', // Deprecated: Use pet_notes_1 to pet_notes_4 instead
                'pet_notes_1': 'Second pet for same owner',
                'pet_notes_2': '',
                'pet_notes_3': '',
                'pet_notes_4': '',
                'vet_name': 'Dr. Smith',
                'vet_business_name': 'Pets Veterinary Clinic',
                'vet_email': 'drsmith@example.com',
                'vet_phone': '555-123-4567',
                'vaccine_name_1': 'bordetella',
                'vaccine_expiration_date_1': '2025-08-15',
                ...emptyVaccineColumns(2),
                'authorized_contact_first_name_1': 'Maria',
                'authorized_contact_last_name_1': 'Rodriguez',
                'authorized_contact_email_1': 'maria.rodriguez@example.com',
                'authorized_contact_phone_1': '555-111-2222',
                'authorized_contact_relationship_id_1': '2',
                'authorized_contact_emergency_contact_1': 'true',
                'authorized_contact_first_name_2': 'Carlos',
                'authorized_contact_last_name_2': 'Gomez',
                'authorized_contact_email_2': 'carlos.gomez@example.com',
                'authorized_contact_phone_2': '555-333-4444',
                'authorized_contact_relationship_id_2': '3',
                'authorized_contact_emergency_contact_2': 'false',
                'authorized_contact_first_name_3': '',
                'authorized_contact_last_name_3': '',
                'authorized_contact_email_3': '',
                'authorized_contact_phone_3': '',
                'authorized_contact_relationship_id_3': '',
                'authorized_contact_emergency_contact_3': '',
                'authorized_contact_first_name_4': '',
                'authorized_contact_last_name_4': '',
                'authorized_contact_email_4': '',
                'authorized_contact_phone_4': '',
                'authorized_contact_relationship_id_4': '',
                'authorized_contact_emergency_contact_4': ''
            },
            {
                'first_name*': 'John',
                'last_name*': 'Q',
                'email*': 'john@cuddlesapp.com',
                'phone*': '123-456-7890',
                'address': '456 Oak Ave',
                'address_line_two': '',
                'city': 'Orlando',
                'state': 'FL',
                'zipcode': '32801',
                'country': 'US',
                'pet_name*': 'Cupcake',
                'pet_type*': 'dogs',
                'pet_size*': 'small',
                'pet_breed': 'Shiba Inu',
                'pet_gender': 'female',
                'pet_birthdate': '2018-11-08',
                'pet_color': '',
                'pet_weight': '12',
                // 'pet_notes': 'Shy with strangers', // Deprecated: Use pet_notes_1 to pet_notes_4 instead
                'pet_notes_1': 'Shy with strangers',
                'pet_notes_2': '',
                'pet_notes_3': '',
                'pet_notes_4': '',
                'vet_name': '',
                'vet_business_name': '',
                'vet_email': '',
                'vet_phone': '',
                ...emptyVaccineColumns(1),
                'authorized_contact_first_name_1': 'Sarah',
                'authorized_contact_last_name_1': 'Johnson',
                'authorized_contact_phone_1': '321-654-9870',
                'authorized_contact_relationship_id_1': '5',
                'authorized_contact_emergency_contact_1': 'true',
                'authorized_contact_first_name_2': '',
                'authorized_contact_last_name_2': '',
                'authorized_contact_phone_2': '',
                'authorized_contact_relationship_id_2': '',
                'authorized_contact_emergency_contact_2': '',
                'authorized_contact_first_name_3': '',
                'authorized_contact_last_name_3': '',
                'authorized_contact_phone_3': '',
                'authorized_contact_relationship_id_3': '',
                'authorized_contact_emergency_contact_3': '',
                'authorized_contact_first_name_4': '',
                'authorized_contact_last_name_4': '',
                'authorized_contact_phone_4': '',
                'authorized_contact_relationship_id_4': '',
                'authorized_contact_emergency_contact_4': ''
            }
        ];

        const fileName = 'Cuddles Import Template-Clients';
        const exportType = exportFromJSON.types.csv;
        exportFromJSON({ data, fileName, exportType });
    };

    // The scroll listener
    const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const div = event.target as HTMLDivElement;

        setScrollPos(div.scrollLeft);
    };

    const normalizeWeight = (value?: string): string => {
        if (!value || value.trim() === '') {
            return '';
        }

        const numberValue = Number(value.replace(',', '.'));

        if (isNaN(numberValue)) {
            return '';
        }

        return Math.trunc(numberValue).toString();
    };

    const RELATIONSHIP_MAP: Record<number, string> = {
        1: 'Child',
        2: 'Family',
        3: 'Friend',
        4: 'Parent',
        5: 'Partner',
        6: 'Sibling',
        7: 'Co-Owner'
    };

    const getRelationshipLabel = (relationshipId?: string | number): string => {
        const id = Number(relationshipId);
        return RELATIONSHIP_MAP[id] ?? RELATIONSHIP_MAP[3];
    };

    return (
        <>
            {!uploading ? (
                <WizardPage
                    isLoading={saving}
                    step={step}
                    items={[
                        {
                            title: 'Upload File'
                        },
                        {
                            title: 'Preview & Import'
                        }
                    ]}
                    onSave={saveHandler}
                    onNext={nextHandler}
                    onBack={() => setStep(prev => prev - 1)}
                    closePath=""
                    isFooter={true}
                    onClickClosePath={() => {
                        resetImportState();
                        props.onClose?.();
                    }}
                >
                    {step === 1 && (
                        <Grid
                            container
                            justifyContent="center"
                        >
                            <Grid
                                item
                                md={6}
                                xs={12}
                            >
                                <Fragment>
                                    <Box className={classes.borderContainer}>
                                        <Typography className={classes.firstText}>Import Clients</Typography>
                                        <Typography className={classes.secondText}>
                                            For an easy, successful import{' '}
                                            <span
                                                style={styles.downloadText}
                                                onClick={exportToCsv}
                                            >
                                                download
                                            </span>{' '}
                                            our pre-designed template and fill it out.
                                        </Typography>
                                        <Dropzone
                                            accept={['text/csv']}
                                            maxFiles={1}
                                            onDrop={acceptedFiles => {
                                                if (acceptedFiles.length > 0) {
                                                    setSelectedFile(acceptedFiles[0]);
                                                    handleFileParse(acceptedFiles[0]);
                                                }
                                            }}
                                            onDropRejected={() => {
                                                const errors = validateFormat();
                                                setErrors(errors);
                                            }}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <>
                                                    <div
                                                        {...getRootProps()}
                                                        style={Object.assign({}, styles.zone)}
                                                    >
                                                        <input {...getInputProps()} />
                                                        <Img
                                                            className={classes.image}
                                                            src="/images/clients/fileImport.svg"
                                                        />
                                                        <Typography className={classes.thirdText}>
                                                            Select your CSV file or drag and drop here
                                                        </Typography>
                                                        <Button
                                                            label={'+ Add CSV File'}
                                                            className={classes.csvButton}
                                                        />
                                                    </div>
                                                    {selectedFile ? (
                                                        <div>
                                                            <Typography className={classes.fourthText}>
                                                                Files Upload
                                                            </Typography>
                                                            <div style={styles.fileContainer}>
                                                                <div style={styles.fileSubcontainer}>
                                                                    <div style={styles.iconCenter}>
                                                                        <Img
                                                                            className={classes.checkFile}
                                                                            src="/images/clients/checkFile.svg"
                                                                        />
                                                                    </div>
                                                                    <div style={styles.iconCenter}>
                                                                        <Img
                                                                            className={classes.iconFile}
                                                                            src="/images/clients/fileIcon.svg"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div style={styles.fileName}>
                                                                            {selectedFile.name}
                                                                        </div>
                                                                        <div style={styles.fileSize}>
                                                                            {formatFileSize(selectedFile.size)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    style={styles.fileSubcontainerDelete}
                                                                    className="delete-button"
                                                                    onClick={() => {
                                                                        setSelectedFile(null);
                                                                        setFile(false);
                                                                        setParsedCsvData([]);
                                                                    }}
                                                                >
                                                                    <div style={styles.iconCenter}>
                                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                                    </div>
                                                                    <div
                                                                        className="delete-label"
                                                                        style={styles.deleteText}
                                                                    >
                                                                        Delete
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}
                                                </>
                                            )}
                                        </Dropzone>
                                    </Box>

                                    <div
                                        style={{
                                            display: errors.some(error => error.fieldName === 'file')
                                                ? 'inline-block'
                                                : 'none',
                                            ...errorStyle
                                        }}
                                        className={classes.errorMessage}
                                    >
                                        {errors.find(error => error.fieldName === 'file')?.errorMessage}
                                    </div>

                                    <Box className={classes.borderContainerContact}>
                                        <Img
                                            className={classes.imageContact}
                                            src="/images/clients/contactIcon.svg"
                                        />
                                        <div>
                                            <Typography className={classes.firstHelpText}>Need help?</Typography>
                                            <Typography className={classes.secondHelpText}>
                                                If you need assistance with the process of importing your contacts, our
                                                support team is ready to help you.
                                            </Typography>
                                            <Typography className={classes.thirdHelpText}>
                                                <a
                                                    style={{
                                                        color: '#222222',
                                                        textDecoration: 'auto'
                                                    }}
                                                    href="mailto:help@cuddlesapp.com"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Contact Us
                                                    <FontAwesomeIcon icon={faAngleRight} />
                                                </a>
                                            </Typography>
                                        </div>
                                    </Box>
                                </Fragment>
                            </Grid>
                        </Grid>
                    )}

                    {step === 2 && (
                        <Grid
                            container
                            justifyContent="center"
                        >
                            <Grid
                                item
                                md={10}
                                xs={12}
                                className={classes.tableContainerGrid}
                            >
                                <Fragment>
                                    <Typography className={classes.firstText}>Client Import Preview</Typography>
                                    <Typography className={classes.secondText}>
                                        Please make sure that all the data you are importing is correct. Remember that{' '}
                                        <br />
                                        any data that does not have the correct format will not be imported.
                                    </Typography>
                                    {hasInvalidColors && (
                                        <Typography
                                            style={{
                                                fontSize: '14px',
                                                color: '#EF4F57',
                                                marginTop: '12px',
                                                marginBottom: '8px',
                                                fontWeight: 500
                                            }}
                                        >
                                            ⚠️ Pet colors marked with warning are invalid and won&apos;t be imported.
                                        </Typography>
                                    )}
                                    <Grid
                                        container
                                        className={classes.tableContainer}
                                    >
                                        {arrowLeft ? (
                                            <Grid
                                                item
                                                className={classes.scrollArrow}
                                            >
                                                <Img
                                                    onClick={() => {
                                                        handleHorizantalScrollLeft();
                                                    }}
                                                    className={classes.arrowIcon}
                                                    src="/images/clients/arrowLeft.svg"
                                                />
                                            </Grid>
                                        ) : (
                                            ''
                                        )}
                                        <Grid
                                            item
                                            xs={12}
                                            md={11}
                                        >
                                            <div
                                                ref={elementRef}
                                                onScroll={handleScroll}
                                                className={clsx(classes.boxGridContainer, {
                                                    scrolled: scrollPos > 0
                                                })}
                                            >
                                                <table style={styles.tableStyle}>
                                                    <thead>
                                                        <tr style={styles.trTableHeader}>
                                                            <th style={styles.thTable}>First Name</th>
                                                            <th style={styles.thTable}>Last Name</th>
                                                            <th style={styles.thTable}>Email</th>
                                                            <th style={styles.thTable}>Address</th>
                                                            <th style={styles.thTable}>Address Line 2</th>
                                                            <th style={styles.thTable}>City</th>
                                                            <th style={styles.thTable}>State</th>
                                                            <th style={styles.thTable}>Zip Code</th>
                                                            <th style={styles.thTable}>Phone</th>
                                                            <th style={styles.thTable}>Pet Name</th>
                                                            <th style={styles.thTable}>Pet Breed</th>
                                                            <th style={styles.thTable}>Pet Type</th>
                                                            <th style={styles.thTable}>Pet Size</th>
                                                            <th style={styles.thTable}>Pet Gender</th>
                                                            <th style={styles.thTable}>Pet Birthdate</th>
                                                            <th style={styles.thTable}>Pet Color</th>
                                                            <th style={styles.thTable}>Pet Weight</th>
                                                            {/* <th style={styles.thTable}>Pet Notes</th> */}
                                                            <th style={styles.thTable}>Pet Notes 1</th>
                                                            <th style={styles.thTable}>Pet Notes 2</th>
                                                            <th style={styles.thTable}>Pet Notes 3</th>
                                                            <th style={styles.thTable}>Pet Notes 4</th>
                                                            <th style={styles.thTable}>Vet Name</th>
                                                            <th style={styles.thTable}>Vet Business</th>
                                                            <th style={styles.thTable}>Vet Email</th>
                                                            <th style={styles.thTable}>Vet Phone</th>
                                                            <th style={styles.thTable}>Vaccines</th>
                                                            <th style={styles.thTable}>Contact 1</th>
                                                            <th style={styles.thTable}>Contact 2</th>
                                                            <th style={styles.thTable}>Contact 3</th>
                                                            <th style={styles.thTable}>Contact 4</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pageClients &&
                                                            pageClients.map(
                                                                (
                                                                    parsedData: ClientCsv,
                                                                    index: React.Key | null | undefined
                                                                ) => (
                                                                    <tr
                                                                        style={styles.trTableContent}
                                                                        key={index}
                                                                    >
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.first_name}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.last_name}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.email}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.address}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.address_line_two}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.city}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.state}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.zipcode}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.phone}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_name}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_breed}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_type}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_size}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_gender}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_birthdate || ''}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_color ? (
                                                                                isValidColor(
                                                                                    parsedData.pet_color,
                                                                                    parsedData.pet_type
                                                                                ) ? (
                                                                                    parsedData.pet_color
                                                                                ) : (
                                                                                    <span style={{ color: '#EF4F57' }}>
                                                                                        {parsedData.pet_color} ⚠️
                                                                                    </span>
                                                                                )
                                                                            ) : (
                                                                                ''
                                                                            )}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {normalizeWeight(parsedData.pet_weight)}
                                                                        </td>
                                                                        {/* <td style={styles.tdTable}>
                                                                            {parsedData.pet_notes}
                                                                        </td> */}
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_notes_1}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_notes_2}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_notes_3}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.pet_notes_4}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.vet_name}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.vet_business_name}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.vet_email}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.vet_phone}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {getVaccineLabels(parsedData).map(
                                                                                (label, labelIndex) => (
                                                                                    <div
                                                                                        key={labelIndex}
                                                                                        style={styles.vaccineLabel}
                                                                                    >
                                                                                        {label}
                                                                                    </div>
                                                                                )
                                                                            )}
                                                                        </td>
                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.authorized_contact_first_name_1 &&
                                                                            parsedData.authorized_contact_last_name_1 &&
                                                                            parsedData.authorized_contact_phone_1
                                                                                ? `${
                                                                                      parsedData.authorized_contact_first_name_1
                                                                                  } ${
                                                                                      parsedData.authorized_contact_last_name_1
                                                                                  }${
                                                                                      parsedData.authorized_contact_email_1
                                                                                          ? ` (${parsedData.authorized_contact_email_1})`
                                                                                          : ''
                                                                                  } - ${
                                                                                      parsedData.authorized_contact_phone_1
                                                                                  }${
                                                                                      String(
                                                                                          parsedData.authorized_contact_emergency_contact_1
                                                                                      ).toLowerCase() === 'true'
                                                                                          ? ' (Emergency)'
                                                                                          : ''
                                                                                  } - ${getRelationshipLabel(
                                                                                      parsedData.authorized_contact_relationship_id_1
                                                                                  )}`
                                                                                : ''}
                                                                        </td>

                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.authorized_contact_first_name_2 &&
                                                                            parsedData.authorized_contact_last_name_2 &&
                                                                            parsedData.authorized_contact_phone_2
                                                                                ? `${
                                                                                      parsedData.authorized_contact_first_name_2
                                                                                  } ${
                                                                                      parsedData.authorized_contact_last_name_2
                                                                                  }${
                                                                                      parsedData.authorized_contact_email_2
                                                                                          ? ` (${parsedData.authorized_contact_email_2})`
                                                                                          : ''
                                                                                  } - ${
                                                                                      parsedData.authorized_contact_phone_2
                                                                                  }${
                                                                                      String(
                                                                                          parsedData.authorized_contact_emergency_contact_2
                                                                                      ).toLowerCase() === 'true'
                                                                                          ? ' (Emergency)'
                                                                                          : ''
                                                                                  } - ${getRelationshipLabel(
                                                                                      parsedData.authorized_contact_relationship_id_2
                                                                                  )}`
                                                                                : ''}
                                                                        </td>

                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.authorized_contact_first_name_3 &&
                                                                            parsedData.authorized_contact_last_name_3 &&
                                                                            parsedData.authorized_contact_phone_3
                                                                                ? `${
                                                                                      parsedData.authorized_contact_first_name_3
                                                                                  } ${
                                                                                      parsedData.authorized_contact_last_name_3
                                                                                  }${
                                                                                      parsedData.authorized_contact_email_3
                                                                                          ? ` (${parsedData.authorized_contact_email_3})`
                                                                                          : ''
                                                                                  } - ${
                                                                                      parsedData.authorized_contact_phone_3
                                                                                  }${
                                                                                      String(
                                                                                          parsedData.authorized_contact_emergency_contact_3
                                                                                      ).toLowerCase() === 'true'
                                                                                          ? ' (Emergency)'
                                                                                          : ''
                                                                                  } - ${getRelationshipLabel(
                                                                                      parsedData.authorized_contact_relationship_id_3
                                                                                  )}`
                                                                                : ''}
                                                                        </td>

                                                                        <td style={styles.tdTable}>
                                                                            {parsedData.authorized_contact_first_name_4 &&
                                                                            parsedData.authorized_contact_last_name_4 &&
                                                                            parsedData.authorized_contact_phone_4
                                                                                ? `${
                                                                                      parsedData.authorized_contact_first_name_4
                                                                                  } ${
                                                                                      parsedData.authorized_contact_last_name_4
                                                                                  }${
                                                                                      parsedData.authorized_contact_email_4
                                                                                          ? ` (${parsedData.authorized_contact_email_4})`
                                                                                          : ''
                                                                                  } - ${
                                                                                      parsedData.authorized_contact_phone_4
                                                                                  }${
                                                                                      String(
                                                                                          parsedData.authorized_contact_emergency_contact_4
                                                                                      ).toLowerCase() === 'true'
                                                                                          ? ' (Emergency)'
                                                                                          : ''
                                                                                  } - ${getRelationshipLabel(
                                                                                      parsedData.authorized_contact_relationship_id_4
                                                                                  )}`
                                                                                : ''}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {pagination}
                                        </Grid>
                                        {arrowRight ? (
                                            <Grid
                                                item
                                                className={classes.scrollArrow}
                                            >
                                                <Img
                                                    onClick={() => {
                                                        handleHorizantalScrollRight();
                                                    }}
                                                    className={classes.arrowIcon}
                                                    src="/images/clients/arrowRight.svg"
                                                />
                                            </Grid>
                                        ) : (
                                            ''
                                        )}
                                    </Grid>
                                </Fragment>
                            </Grid>
                        </Grid>
                    )}
                </WizardPage>
            ) : (
                <Grid
                    container
                    justifyContent="center"
                    className={classes.statusPage}
                >
                    <Box className={classes.statusBody}>
                        <div style={{ justifySelf: 'center' }}>
                            <Img
                                className={classes.statusIcon}
                                src={icon}
                            />
                        </div>
                        <Typography className={classes.statusTitle}>{title}</Typography>

                        {progress.event != 'import_finished' && (
                            <ProgressBar
                                percentage={progress.percentage}
                                className={classes.progressBar}
                            />
                        )}

                        <Typography className={classes.statusDescription}>{content}</Typography>
                    </Box>
                    <Box className={classes.statusFooter}>
                        <Button
                            className={classes.button}
                            color="black"
                            label={textButton}
                            onClick={() => {
                                resetImportState();
                                props.onClose?.();
                            }}
                        />
                    </Box>
                </Grid>
            )}
        </>
    );
};

export default ClientBulkUpload;
